import * as Sentry from '@sentry/react'
import React from 'react'
import {
  Routes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import type { SentryEnv } from '../../vite-env.d.ts'
import CONSTANTS from 'utils/constants.js'
/**
 * Initialise sentry, used for
 * - Crash analytics
 * - Performance monitoring
 * - Debugging
 */
export const initialiseSentry = () => {
  const sentryEnv = CONSTANTS.VITE_SENTRY_ENV
  const { tracesSampleRate } = getEnvProps(sentryEnv)

  Sentry.init({
    enabled: CONSTANTS.VITE_SENTRY_ENABLED,
    dsn: 'https://b7b7bcf682aa4790a25d3bebc0f46731@o1072246.ingest.sentry.io/6070751',
    beforeSend(event) {
      // Get the URL from the event
      const url = event.request?.url;
  
      // If the URL exists, sanitize and filter it
      if (url && event.request?.url) {
        const sanitizedUrl = sanitizeAndFilterUrl(url);
        // If the sanitized URL is not null, set it as the new URL
        if (sanitizedUrl) {
          event.request.url = sanitizedUrl;
        }
      }
  
      return event;
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration(),
    ],

    // tracesSampleRate at 1.0 to captures 100%
    // of transactions for performance monitoring
    // in our development environment
    tracesSampleRate,
    normalizeDepth: 10,
    environment: sentryEnv,
    release: CONSTANTS.VITE_RELEASE_NAME
  })

  return Sentry.withSentryReactRouterV6Routing(Routes)
}

type EnvProps = {
  tracesSampleRate: number
}
const getEnvProps = (env: SentryEnv): EnvProps => {
  const tracesSampleRate = getTracesSampleRate(env)

  return {
    tracesSampleRate
  }
}

const getTracesSampleRate = (env: SentryEnv): number => {
  switch (env) {
    case 'Dev':
      return 0
    case 'Staging':
      return 0.1
    case 'Prod':
      return 0.01
    default:
      return 0
  }
}

// Define a custom route matcher function
function sanitizeAndFilterUrl(url: string): string | null {
  // Define the pattern for MongoDB ObjectId identifiers
  const objectIdPattern = /[0-9a-fA-F]{24}/;

  // Replace all occurrences of MongoDB ObjectId identifiers with a placeholder
  const sanitizedUrl = url.replace(objectIdPattern, ':id');

  // Return the sanitized URL
  return sanitizedUrl;
}

export default initialiseSentry
